import { Button } from '@/components/ui'
import { SEO } from '@/components/seo'
import { GradientHero } from '@/components/layout'

const guarantees = [
  {
    title: 'Your price is unchanged',
    body: 'Existing customers stay on their current per-adviser pricing as long as they want to. The price is frozen against your account, not against the public price list.',
  },
  {
    title: 'Your features are unchanged',
    body: 'Every feature you have access to today remains accessible tomorrow. Module gating only ever applies to new sign-ups; legacy customers bypass it.',
  },
  {
    title: 'Your data is untouched',
    body: 'No reports, meetings, transcriptions, clients, documents or knowledge base items are affected. We never delete on tier changes: only access can change, and only on opt-in migrations.',
  },
]

const faqs = [
  {
    question: 'Why are you changing pricing?',
    answer:
      'We are introducing modular tiers so new customers can start with just the features they need (Meetings tier from £79/adviser/month) rather than buying the whole platform on day one. This lowers the barrier to adoption and lets firms grow into the full system over time.',
  },
  {
    question: "Do I have to switch to one of the new tiers?",
    answer:
      'No. The default for every existing customer is to stay on your current plan. There is no automatic migration, no "we will move you in 30 days" deadline. You only switch if you actively choose to.',
  },
  {
    question: 'When would I want to switch?',
    answer:
      'If the new Complete tier (£299/adviser/month) gives you features you do not have today (compliance and workflow tooling, with a branded client portal coming soon) and the maths works for your firm, switching may make sense. We will provide an in-app comparison tool so you can see the impact before deciding.',
  },
  {
    question: 'What if I want to add advisers or seats?',
    answer:
      'You add seats at the same per-adviser price you are on today. The legacy price applies to all seats on your subscription, whether they were added before or after the change.',
  },
  {
    question: 'What about my existing custom pricing or discount?',
    answer:
      'Bespoke commercial deals (custom pricing, complimentary access, network discounts) are honoured exactly as before. Your account manager will continue to look after these arrangements directly.',
  },
  {
    question: 'How will you communicate about this?',
    answer:
      'We will send a heads-up email roughly 30 days before any new tiers go live, a reminder a week beforehand, and the launch announcement on the day. Anything that affects you specifically will come from your account manager.',
  },
  {
    question: 'I have a question that is not answered here.',
    answer:
      'Email us at support@wealthspace.ai or speak to your usual contact. We would rather over-explain than leave you uncertain.',
  },
]

export default function PricingUpdatePage() {
  return (
    <>
      <SEO
        title="Pricing update | WealthSpace"
        description="A transparent guide to WealthSpace's modular pricing change. Existing customers: your plan, price and features are unchanged. Read the FAQ."
        url="/pricing-update"
      />

      <GradientHero
        revisionBadge="For existing customers"
        title={
          <>
            Pricing update: <span className="italic text-teal-200">your plan is unchanged</span>
          </>
        }
        subtitle={
          <>
            We are introducing modular pricing for new firms joining WealthSpace. This page explains what is changing, what is not, and the protections we have built in for the customers who are already with us.
          </>
        }
        meta="Updated April 2026"
      />

      {/* Three guarantees */}
      <section className="px-6 pt-16 pb-12 max-w-[1100px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {guarantees.map((g) => (
            <div
              key={g.title}
              className="bg-white border border-teal-200 rounded-2xl p-6 shadow-card"
            >
              <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-accent-teal" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{g.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{g.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQs */}
      <section className="px-6 py-16 bg-slate-50">
        <div className="max-w-[800px] mx-auto">
          <h2 className="font-serif text-3xl sm:text-4xl text-slate-900 text-center mb-10">
            Frequently asked <span className="text-accent-teal italic">questions</span>
          </h2>
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-card">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{faq.question}</h3>
                <p className="text-[15px] text-slate-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Button href="/pricing" variant="secondary">
              See the new tiers
            </Button>
          </div>
        </div>
      </section>
    </>
  )
}
